import React from 'react';
import { Size } from 'types';
import measurement from 'utils/measurement';
import { getScaleFactors } from 'utils/placement';
import { createTextShadowString, createTextStrokeConfig } from 'utils/ui';
import { ITextShadow, Style } from './types';

function convertTextShadowToStyle(
  textShadow: string | ITextShadow | undefined,
) {
  return {
    textShadow:
      typeof textShadow === 'string'
        ? textShadow
        : createTextShadowString(textShadow),
  };
}

export function getCaptionsPositionStyle(
  captionsStyle: Style,
  frameDimensions: Size<number>,
): React.CSSProperties {
  if (!captionsStyle || !frameDimensions) {
    return undefined;
  }

  const position = captionsStyle.get('position');
  const size = captionsStyle.get('size');
  const containerStyle = captionsStyle.get('containerStyle')?.toJS() ?? {};
  const textBoxHeight = captionsStyle.get('textBoxHeight');
  const styleViewport = captionsStyle.get('viewport')?.toJS();

  if (!styleViewport) {
    return undefined;
  }

  // Values in Style are pixel values relative to styleViewport
  // Scale them proportionally to frameDimensions
  const { scaleX, scaleY } = getScaleFactors(styleViewport, frameDimensions);

  const scaleValue = (val: number | string | undefined, isVh = false) => {
    if (val === undefined) return undefined;

    // If it's a string, use measurement (for edge cases)
    if (typeof val === 'string') {
      return measurement(val)
        .toUnit('px', frameDimensions)
        .toString();
    }

    // Value is already in pixels relative to styleViewport
    const scale = isVh ? scaleY : scaleX;
    return `${val * scale}px`;
  };

  const left = scaleValue(position?.get('left'));
  const top = scaleValue(position?.get('top'), true);
  const width = scaleValue(size?.get('width'));
  const height = textBoxHeight ? scaleValue(textBoxHeight, true) : undefined;

  // Container style values are also pixel values relative to styleViewport
  const containerStylePx = {
    paddingLeft: scaleValue(containerStyle.paddingLeft),
    paddingTop: scaleValue(containerStyle.paddingTop),
    paddingRight: scaleValue(containerStyle.paddingRight),
    paddingBottom: scaleValue(containerStyle.paddingBottom),
    fontSize: scaleValue(containerStyle.fontSize),
  };

  return {
    ...containerStyle,
    ...containerStylePx,
    ...convertTextShadowToStyle(containerStyle?.textShadow),
    // @ts-ignore
    ...createTextStrokeConfig(containerStyle?.textOutline),
    height,
    left,
    position: 'absolute' as const,
    top,
    width,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  };
}
